/**
 * ChartRenderer — Single ECharts rendering component.
 *
 * Receives unified ChartData, converts to ECharts option via option builders,
 * and renders through the useECharts hook.
 *
 * Measures its own container width so that option builders can auto-rotate
 * x-axis labels and auto-calculate y-axis nameGap using measureText.
 *
 * Handles all chart types except KPI (custom React) and Table (Fluent DataGrid).
 */
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useECharts } from './useECharts';
import { buildOption, applyChartStyle, type ChartData } from './optionBuilders';
import { getChartTheme } from './theme';
import type { ChartEditorState } from '../../edit/visualConfig';
import type { FieldMappingMap } from '../../../api/types';

export interface ChartRendererProps {
  /** Unified chart data from the server */
  data: ChartData;
  /** Dark mode flag */
  isDark: boolean;
  /** Enable animation (false for dashboard, true for edit preview) */
  animated?: boolean;
  /** Editor state overrides (edit mode or rebuilt from saved config) */
  editorState?: ChartEditorState;
  /** True when in edit mode — forces full chart update on every config change */
  isEditMode?: boolean;
  /** Field mappings for human-readable axis labels */
  fieldMappings?: FieldMappingMap | null;
  /** Chart ID — forwarded to useECharts for 'echart-rendered' event */
  chartId?: string;
}

/**
 * Hook to track an element's width via ResizeObserver.
 * Returns [ref, width] — width defaults to 0 until first measurement.
 *
 * Width updates are debounced (200ms) so that rapid resize events during
 * grid drag/resize don't trigger expensive option rebuilds on every pixel.
 * The initial measurement is applied immediately (no debounce).
 */
function useContainerWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Initial measurement — apply immediately
    setWidth(el.clientWidth);

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        // Debounce state updates to avoid option rebuilds during drag
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          setWidth((prev) => (Math.abs(prev - w) > 2 ? w : prev));
        }, 200);
      }
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return [ref, width];
}

/**
 * Renders any ECharts-compatible chart type from unified ChartData.
 *
 * Usage:
 * ```tsx
 * <ChartRenderer data={chartData} isDark={isDark} animated={false} />
 * ```
 */
const ChartRenderer: React.FC<ChartRendererProps> = ({
  data,
  isDark,
  animated = false,
  editorState,
  isEditMode = false,
  fieldMappings,
  chartId,
}) => {
  const [containerRef, containerWidth] = useContainerWidth();
  const theme = useMemo(() => getChartTheme(isDark), [isDark]);

  const option = useMemo(() => {
    // Override data.chartType with editor state chart type for live preview
    const effectiveData = editorState
      ? { ...data, chartType: editorState.chartType }
      : data;
    const baseOption = buildOption(effectiveData, theme, animated, containerWidth || 400);
    if (!baseOption) return null;
    // Apply visual config overrides when present (edit mode)
    if (editorState) {
      return applyChartStyle(baseOption, editorState.chartStyle, isDark, fieldMappings, undefined, undefined, editorState.chartType);
    }
    return baseOption;
  }, [data, theme, animated, containerWidth, editorState, isDark, fieldMappings]);

  // Force full update only in edit mode (live preview) — not on dashboard
  // where visual config is stable after initial reconstruction.
  const chartRef = useECharts({ option, isDark, animated, forceFullUpdate: isEditMode, chartId });

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', minHeight: 250 }}>
      <div
        ref={chartRef}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
};

export default ChartRenderer;